import { kinsokuGenerator, isEmojiAt, leaderChars, trailerChars } from "../kinsoku";
import { parseMarkdownToJson, richTextIterator, type RichFragment } from "./richText";
import type { Rectangle, RenderingText, DrawMethod } from "./typeSetting";
import { getVerticalGlyphAdjust, type GlyphAlign, type VerticalGlyphAdjust } from "./verticalGlyphAdjust";

export function drawVerticalText(
  context: CanvasRenderingContext2D, 
  method: DrawMethod, 
  r: Rectangle, 
  text: string, 
  baselineSkip: number, 
  charSkip: number,
  rubySize: number,
  rubyDistance: number, 
  m?: RenderingText, 
  autoNewline?: boolean
): void {
  if (!m) {
    m = measureVerticalText(context, r.height, text, baselineSkip, charSkip, autoNewline);
  }

  const adjust = getVerticalGlyphAdjust(context);

  let cursorX = r.x + r.width - baselineSkip * 0.5; // center of the text
  for (const line of m.verticalLines!) {
    let lineH = 0;
    let prev = null;
    for (const frag of line) {
      let startH: number, endH: number;
      const charScale = frag.size ?? 1;
      ({lineH, prev, startH, endH} = drawFragment(context, r, cursorX, lineH, charSkip, method, frag, charScale, false, prev, adjust));
      if (frag.ruby) {
        /*
        context.strokeStyle = "red";
        context.beginPath();
        context.moveTo(cursorX - baselineSkip * 0.5, r.y + startH);
        context.lineTo(cursorX + baselineSkip * 0.5, r.y + startH);
        context.stroke();
        context.strokeStyle = "green";
        context.beginPath();
        context.moveTo(cursorX - baselineSkip * 0.5, r.y + endH);
        context.lineTo(cursorX + baselineSkip * 0.5, r.y + endH);
        context.stroke();
        */

        const region = endH - startH;
        const rubyCharSkip = region / frag.ruby.length;
        const rubyX = cursorX + baselineSkip * rubyDistance;
        const rubyFragment = {
          chars: frag.ruby,
          color: frag.color
        }
        drawFragment(context, r, rubyX, startH, rubyCharSkip, method, rubyFragment, rubySize, true, null, adjust);
      }
    }
    cursorX -= baselineSkip;
  }
}

function drawLine(context: CanvasRenderingContext2D, x: number, y: number, w: number, color: string): void {;
  context.strokeStyle = color;
  context.beginPath();
  context.moveTo(x, y);
  context.lineTo(x+w, y);
  context.stroke();
}

function drawFragment(
  context: CanvasRenderingContext2D, 
  r: Rectangle, 
  cursorX: number, 
  lineH: number, 
  charSkip: number, 
  method: DrawMethod,
  frag: RichFragment,
  charScale: number,
  justify: boolean,
  prev: string | null,
  adjust: VerticalGlyphAdjust): { lineH: number, prev: string | null, startH: number, endH: number } {

  const scaledCharSkip = charSkip * (justify ? 1 : charScale);
  const emW = context.measureText('田').width; // このフォントの全角幅
  let startH: number | null = null;
  let endH: number | null = null;

  if (frag.romanHanging) {
    const s = frag.chars.join('');
    const tm: TextMetrics = context.measureText(s);
    const cw = tm.width;
    const ch = tm.actualBoundingBoxAscent + tm.actualBoundingBoxDescent;
    const adjustDown = charSkip * 0.15;// 日本語文字の平均的なdescent分のヒューリスティック;

    if (startH === null) { startH = lineH; }
    endH = lineH + cw;

    context.save();
    context.translate(cursorX - ch * 0.5, r.y + lineH + adjustDown); 
    context.rotate(90 * Math.PI / 180);
    context.scale(charScale, charScale);
    if (method === "fill") {
      if (frag.color) { context.fillStyle = frag.color; }
      context.fillText(s, 0, 0);
    } else if (method === "stroke") {
      if (frag.color) { context.strokeStyle = frag.color; }
      context.strokeText(s, 0, 0);
    }
    context.restore();
    return {lineH: lineH + cw, prev, startH, endH};
  }

  // align: 全角幅(emW)の枠に対する字形の寄せ方。
  // プロポーショナルフォントでは括弧類が半角幅になっていることがあり、
  // 字送り幅の中央に置くと回転後の位置が全角字形とずれる。
  // 始め括弧は右寄せ、終わり括弧は左寄せにすると、全角字形(cw == emW)では従来と同一のまま、
  // 半角字形でも全角字形の該当する半分と同じ位置に収まる。
  function drawRotatedChar(angle: number, ax: number, ay: number, xflip: number, yflip: number, s: string, align: GlyphAlign): void {
    const tm: TextMetrics = context.measureText(s);
    const cw = tm.width;
    const originX = align === "right" ? emW * 0.5 - cw : align === "left" ? -emW * 0.5 : -cw * 0.5;

    if (startH === null) {
      startH = lineH + charSkip - tm.actualBoundingBoxAscent; 
    }
    endH = lineH + charSkip + tm.actualBoundingBoxDescent;

    const pivotX = cursorX;
    // ヒューリスティックとして、charScaleが大きいほどすこし上にずらす(charScale==1なら0)
    const scaleOffset = (1 - charScale) * charSkip * 0.15;
    const pivotY = r.y + lineH + scaledCharSkip * 0.5 + scaleOffset; // 空間の中央

    context.save();
    context.translate(pivotX, pivotY);

    // drawLine(context, -scaledCharSkip * 0.5, 0, scaledCharSkip, "blue"); // 空間の中心
    // drawLine(context, -scaledCharSkip * 0.5, scaledCharSkip * 0.5, scaledCharSkip, "green");

    context.scale(charScale, charScale); // 以下charSkip,cwにはcharScaleが掛かっている
    context.translate(ax * charSkip, ay * charSkip); // オフセット
    context.rotate(angle * Math.PI / 180);
    context.scale(xflip, yflip);

    // フォントAPIは左下から始まるので、文字の中心に合わせるために調整
    context.translate(originX, charSkip * 0.5);
    if (method === "fill") {
      if (frag.color) { context.fillStyle = frag.color; }
      context.fillText(s,0,0);
    } else if (method === "stroke") {
      if (frag.color) { context.strokeStyle = frag.color; }
      context.strokeText(s,0,0);
    }
    context.restore();
  }

  function drawChar(ax: number, ay: number, s: string) {
    drawRotatedChar(0, ax, ay, 1, 1, s, "center");
  }

  for (const c of frag.chars) {
    // ヒューリスティック、源暎アンチックを基準にしているが、
    // 源暎エムゴなどでは合わないこともあるので若干緩和している
    lineH += limitedKerning(prev, c) * charSkip * charScale;
    switch (true) {
      case /[、。]/.test(c):
        drawChar(...adjust.punctuation, c);
        break;
      case /[“]/.test(c):
        drawRotatedChar(0, ...adjust.doubleQuoteOpen, -1, 1, "〝", "center");
        break;
      case /[”]/.test(c):
        drawRotatedChar(0, ...adjust.doubleQuoteClose, -1, 1, "〟", "center");
        break;
      case /[ぁぃぅぇぉっゃゅょゎァィゥェォッャュョヵヶ]/.test(c):
        drawChar(...adjust.smallKana, c);
        break;
      case /[「『]/.test(c):
        drawRotatedChar(90, ...adjust.cornerBracketOpen, 1, 1, c, "right");
        break;
      case /[：；]/.test(c):
        drawRotatedChar(90, ...adjust.colon, 1, 1, c, "center");
        break;
      case /[」』]/.test(c):
        drawRotatedChar(90, ...adjust.cornerBracketClose, 1, 1, c, "left");
        break;
      case /[＜（【〔≪｛]/.test(c):
        drawRotatedChar(90, ...adjust.bracketOpen, 1, 1, c, "right");
        break;
      case /[＞）】〕≫｝]/.test(c):
        drawRotatedChar(90, ...adjust.bracketClose, 1, 1, c, "left");
        break;
      case /[ー]/.test(c):
        drawRotatedChar(90, ...adjust.prolongedSound, 1, -1, c, "center");
        break;
      case /[…]/.test(c):
        drawRotatedChar(90, ...adjust.ellipsis, 1, -1, c, "center");
        break;
      case /[―〰]/.test(c):
        drawRotatedChar(90, ...adjust.dash, 1, -1, c, "center");
        break;
      case /[～〜]/.test(c): // U+FF5E, U+301C。フォントの縦書き字形と同じ向きになるよう上下反転
        drawRotatedChar(90, ...adjust.wave, 1, -1, c, "center");
        break;
      case isEmojiAt(c, 0):
        drawChar(0, 0, c);
        break;
      default:
        drawChar(0, 0, c);
        break;
    }
    lineH += scaledCharSkip;
    prev = c;
  }

  return {lineH, prev, startH: startH!, endH: endH!};
}

export function measureVerticalText(
  context: CanvasRenderingContext2D, 
  maxHeight: number, 
  text: string, 
  baselineSkip: number, 
  charSkip: number, 
  autoNewline?: boolean
): RenderingText {
  if (!autoNewline) { maxHeight = Infinity; }

  function calcHeight(ca: RichFragment[]): number {
    let h = 0;
    let prev = null;
    for (const frag of ca) {
      if (frag.romanHanging) {
        const m = context.measureText(frag.chars.join(''));
        h += m.width;
      } else {
        for (const c of frag.chars) {
          h += limitedKerning(prev, c);
          const charScale = frag.size ?? 1;
          h += charSkip * charScale;
          prev = c;
        }
      }
    }
    return h;
  }

  const textLines = text.split('\n');
  const a: { size: number; text: RichFragment[]; }[] = [];

  for (const line of textLines) {
    const segments = parseMarkdownToJson(line);
    
    const richIterator = richTextIterator(segments);
    
    const iterator = kinsokuGenerator(
      (s: RichFragment[]) => {
        const h = calcHeight(s);
        return { size: h, wrap: maxHeight < h };
      }, 
      maxHeight,
      richIterator,
      0,
      (c: RichFragment) => c && c.chars && c.chars.length == 1 && leaderChars.has(c.chars[0]),
      (c: RichFragment) => c && c.chars && c.chars.length == 1 && trailerChars.has(c.chars[0]));
    
    for (const result of iterator) {
      a.push({ size: result.size, text: result.buffer });
    }
  }
    
  return {
    height: a.reduce((max, item) => Math.max(max, item.size), 0),
    width: baselineSkip * a.length,
    verticalLines: a.map(item => item.text)
  };
}

function limitedKerning(c0: string | null, c1: string): number {
  if (!c0) { 
    if (/[「『（【〔“]/.test(c1)) { return -0.3; } 
    return 0;
  }
  if (/[、。」』）】〕”]/.test(c0)) {
    if (/[「『（【〔“]/.test(c1)) { return -0.5; } else if (/[、。]/.test(c1)) { return -0.5; }
    return -0.4;
  } else {
    if (/[「『（【〔“]/.test(c1)) { return -0.4; }
  }
  return 0;
}

